"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Shield, Lock, FileText, Users, TriangleAlert, CircleCheck } from "lucide-react";
export default function RiskManagementGovernance() {
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const {
    ref: item1Ref,
    isVisible: item1Visible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const {
    ref: item2Ref,
    isVisible: item2Visible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const {
    ref: item3Ref,
    isVisible: item3Visible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const {
    ref: item4Ref,
    isVisible: item4Visible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const {
    ref: item5Ref,
    isVisible: item5Visible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const {
    ref: item6Ref,
    isVisible: item6Visible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const {
    ref: descRef,
    isVisible: descVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const riskControls = [{
    icon: Shield,
    title: "Segregated Custody",
    description: "Digital assets held with institutional-grade custodians, segregated from operational accounts."
  }, {
    icon: Lock,
    title: "Multi-Signature Authorization",
    description: "All material transactions require multiple authorized signatories and approval workflows."
  }, {
    icon: FileText,
    title: "Position Limits & VaR Monitoring",
    description: "Systematic position sizing with daily Value-at-Risk calculations and exposure tracking."
  }, {
    icon: Users,
    title: "Independent Risk Committee",
    description: "Quarterly review of risk metrics, operational procedures, and compliance frameworks."
  }, {
    icon: TriangleAlert,
    title: "Incident Response Protocol",
    description: "Documented procedures for operational failures, market disruptions, and cybersecurity events."
  }, {
    icon: CircleCheck,
    title: "External Audit & Reporting",
    description: "Annual financial audits and periodic operational reviews conducted by independent firms."
  }];
  const itemRefs = [item1Ref, item2Ref, item3Ref, item4Ref, item5Ref, item6Ref];
  const itemVisibles = [item1Visible, item2Visible, item3Visible, item4Visible, item5Visible, item6Visible];
  return <section id="risk-management-governance" className="bg-background py-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div ref={titleRef} className={`mb-16 ${titleVisible ? "animate-reveal" : "opacity-0"}`}>
          <h2 className="font-display font-semibold text-[clamp(2rem,4.5vw,2.75rem)] leading-[1.3] tracking-tight text-foreground mb-4">
            Risk Management & Governance
          </h2>
          <p className="text-muted-foreground leading-[1.6] max-w-3xl">
            West Indian Digital operates under disciplined risk controls and institutional governance standards.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {}
          <div className="space-y-8">
            {riskControls.map((control, i) => {
            const Icon = control.icon;
            return <div key={i} data-index={i} ref={itemRefs[i]} className={`flex gap-4 ${itemVisibles[i] ? "animate-reveal" : "opacity-0"}`} style={{
              animationDelay: `${i * 100}ms`
            }}>
                  <div className="flex-shrink-0">
                    <Icon className="w-6 h-6 text-accent transition-colors duration-[var(--duration-medium)]" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-[clamp(1.125rem,2.5vw,1.25rem)] leading-[1.4] text-foreground mb-2">
                      {control.title}
                    </h3>
                    <p className="text-muted-foreground leading-[1.6] text-sm">
                      {control.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>

          {}
          <div className="lg:pl-16 lg:border-l border-border">
            <div ref={descRef} className={`space-y-6 ${descVisible ? "animate-reveal" : "opacity-0"}`}>
              <h3 className="font-display font-semibold text-[clamp(1.5rem,3.5vw,2.25rem)] leading-[1.4] text-foreground mb-6">
                Governance Framework
              </h3>
              <p className="text-foreground leading-[1.6]">
                West Indian Digital maintains institutional governance standards aligned with energy infrastructure and digital asset operations.
              </p>
              <p className="text-foreground leading-[1.6]">
                The company employs a segregated operational structure with independent oversight of infrastructure deployment, digital asset trading, and risk management functions.
              </p>
              <p className="text-foreground leading-[1.6]">
                All capital deployment, counterparty selection, and position management decisions are subject to documented approval processes and periodic review by an independent risk committee.
              </p>
              <p className="text-foreground leading-[1.6]">
                Operational procedures are documented and updated quarterly. External audits are conducted annually, with interim reviews for material changes in operations or risk profile.
              </p>
              <p className="text-muted-foreground leading-[1.6] text-sm mt-8">
                West Indian Digital does not provide investment advice or accept client funds. Operations are conducted on a proprietary basis with internal capital.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}